import { useEffect, useRef, useState } from 'react';

export const useShowKeyboardNavigationHint = () => {
	const [showKeyboardNavigation, setShowKeyboardNavigation] = useState(false);
	const timer = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if ((event.key === 'Control' || event.key === 'Meta') && !timer.current) {
				timer.current = setTimeout(() => {
					setShowKeyboardNavigation(true);
					timer.current = null;
				}, 300);
			}
		};

		const handleKeyUp = (event: KeyboardEvent) => {
			if (event.key === 'Control' || event.key === 'Meta') {
				if (timer.current) {
					clearTimeout(timer.current);
					timer.current = null;
				}
				setShowKeyboardNavigation(false);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
			if (timer.current) {
				clearTimeout(timer.current);
			}
		};
	}, []);

	return showKeyboardNavigation;
};

export const usePressedKeys = () => {
	const [pressedKeys, setPressedKeys] = useState<Map<string, KeyboardEvent>>(new Map());

	const handleKeyDown = (e: KeyboardEvent) => {
		setPressedKeys((prevKeys) => new Map(prevKeys).set(e.key.toLowerCase(), e));
	};

	const handleKeyUp = (e: KeyboardEvent) => {
		setPressedKeys((prevKeys) => {
			const newKeys = new Map(prevKeys);
			newKeys.delete(e.key.toLowerCase());
			return newKeys;
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Only run on mount.
	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, []);

	return pressedKeys;
};

type KeyboardShortcutProps = {
	command?: string[];
	shouldFire: boolean;
	callback: (() => void) | undefined;
};

/**
 * Hook to handle keyboard shortcuts.
 *
 * @param command The keyboard shortcut to listen for.
 * @param shouldFire Whether the callback should be fired, can be on depending on open/closed
 * @param callback The function to call when the keyboard shortcut is pressed.
 */
export const useKeyboardShortcut = ({ command, shouldFire = true, callback }: KeyboardShortcutProps) => {
	const pressedKeys = usePressedKeys();

	useEffect(() => {
		if (!shouldFire || !callback) {
			return;
		}

		const isCurrentKeyboardShortcut = command?.every((key) => pressedKeys.has(key));

		if (isCurrentKeyboardShortcut && shouldFire && callback) {
			command?.forEach((key) => {
				pressedKeys.get(key)?.preventDefault();
			});

			// Only allow the dialog to be opened if it is not already open.
			callback();
		}
	}, [callback, command, pressedKeys, shouldFire]);
};
